import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getImage, getNestedSubelementsByType, getPersonName, hasName, useSlsStateContext } from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

export default function useR2RelationshipOptions({
  currentPersonRecord,
  selectedPerson,
  leaveOutCurrentRecordResults,
  referenceFocusedIndex,
  splitScreen,
}) {
  const [t] = useTranslation()
  const [relationshipOptions, setRelationshipOptions] = useState([])
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()

  // Populates the FormAutosuggest with (respectively) Names in current person's record, PrevImage, CurrentImage, and NextImage (about 100 people)
  // Intentionally leaves in duplicates (found in this record, but also on the current/next/prev page)
  useEffect(() => {
    if (thumbnails && zionSlsState?.currentImageArk && zionSlsState?.images) {
      const currentArkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === zionSlsState.currentImageArk)
      const nextArkIndex = currentArkIndex + 1 < thumbnails?.length ? currentArkIndex + 1 : null
      const previousArkIndex = currentArkIndex !== 0 ? currentArkIndex - 1 : null

      const currentImage = getImage(zionSlsState?.images, zionSlsState?.currentImageArk)
      const nextImage = nextArkIndex ? getImage(zionSlsState?.images, thumbnails[nextArkIndex].ark) : null
      const previousImage = previousArkIndex ? getImage(zionSlsState?.images, thumbnails[previousArkIndex]?.ark) : null
      const splitScreenImage =
        splitScreen && referenceFocusedIndex !== currentArkIndex
          ? getImage(zionSlsState?.images, thumbnails[referenceFocusedIndex]?.ark)
          : null
      const getGroupInformation = (persons, groupName, forCurrentPersonRecord = false) => {
        return (
          persons
            .sort((a, b) => (a?.primary > b?.primary ? -1 : 1))
            // Returns the suggestion object used in FormAutoSugggest
            .map((person) => {
              const obj = {
                key: person.id,
                primaryText: getPersonName({ person, justOneName: true }),
                group: groupName,
                recordId: person?.recordId,
              }

              // Add (primary) indicator as secondary text to head of household
              if (person.primary) obj.secondaryText = t('edit-relationships.autosuggest.primary', '(Principal)')

              if (forCurrentPersonRecord && person.partitionKey !== zionSlsState?.currentImageArk) {
                // This intentionally only looks for prev and next images. We're going to make the label generic to all pages later
                let pageNumber
                if (person.partitionKey === previousImage?.partitionKey) pageNumber = previousArkIndex + 1
                if (person.partitionKey === nextImage?.partitionKey) pageNumber = nextArkIndex + 1

                // Add (page #) indicator as secondary text
                if (pageNumber && person.primary)
                  obj.secondaryText = `${obj.secondaryText} ${t(
                    'edit-relationships.autosuggest.pageNumber',
                    '(page {pageNumber})',
                    { pageNumber }
                  )}`
                else if (pageNumber)
                  obj.secondaryText = t('edit-relationships.autosuggest.pageNumber', '(page {pageNumber})', {
                    pageNumber,
                  })
              }
              return obj
            })
            .filter((opt) => hasName(opt.primaryText.trim()))
        )
      }

      const skipCurrentRecordIds = leaveOutCurrentRecordResults
        ? getNestedSubelementsByType(currentPersonRecord, 'PERSON').map((person) => person.id)
        : []
      let relationshipOpts = [
        ...(currentImage?.subElements
          ? currentImage.subElements.flatMap((record) =>
              getGroupInformation(
                getNestedSubelementsByType(record, 'PERSON').filter(
                  (person) => person !== selectedPerson && !skipCurrentRecordIds.includes(person.id)
                ),
                t('edit-relationships.autosuggest.image-group.this', 'IN THIS IMAGE')
              )
            )
          : []),
        ...(currentImage?.floatingElements
          ? getGroupInformation(
              getNestedSubelementsByType({ subElements: currentImage.floatingElements }, 'PERSON').filter(
                (person) => person !== selectedPerson
              ),
              t('edit-relationships.autosuggest.image-group.this', 'IN THIS IMAGE')
            )
          : []),
      ]
      relationshipOpts = relationshipOpts
        .concat(
          splitScreenImage?.subElements
            ? splitScreenImage.subElements?.flatMap((record) =>
                getGroupInformation(
                  getNestedSubelementsByType(record, 'PERSON'),
                  t('edit-relationships.autosuggest.image-group.split-screen', 'NAMES ON SPLIT SCREEN (PAGE {page})', {
                    page: referenceFocusedIndex + 1,
                  })
                )
              )
            : []
        )
        .concat(
          splitScreenImage?.floatingElements
            ? getGroupInformation(
                getNestedSubelementsByType({ subElements: splitScreenImage.floatingElements }, 'PERSON'),
                t('edit-relationships.autosuggest.image-group.split-screen', 'NAMES ON SPLIT SCREEN (PAGE {page})', {
                  page: referenceFocusedIndex + 1,
                })
              )
            : []
        )
      if (currentPersonRecord && !leaveOutCurrentRecordResults) {
        const currentPersonRecordPersons = getNestedSubelementsByType(currentPersonRecord, 'PERSON').filter(
          (person) => person !== selectedPerson
        )

        relationshipOpts = [
          ...getGroupInformation(
            currentPersonRecordPersons,
            t('edit-relationships.autosuggest.record-group', 'IN THIS RECORD'),
            true
          ),
          ...relationshipOpts,
        ]
      }

      setRelationshipOptions(relationshipOpts)
    }
  }, [
    zionSlsState,
    thumbnails,
    selectedPerson,
    t,
    currentPersonRecord,
    referenceFocusedIndex,
    splitScreen,
    leaveOutCurrentRecordResults,
  ])

  return relationshipOptions
}
